/**
 * 侧边栏（CLAUDE.md §功能点 4；LEO 设计 §6.2 侧栏）。
 *
 * 多平台复用：以 `platform` 入参驱动品牌标识与路径前缀。
 *   - 腾讯广告（tencent）：广告命名模板 + 红线规则配置 + 策略配置 + 策略运行记录
 *     + T0广告看板 + DashBoard + 授权更新（7 项，均已实现）
 *   - 华为应用商店（huawei）：广告命名模板 + 红线规则配置（均已实现）+ 策略配置 + 策略运行记录（2 项空页）
 *   - Apple Ads（apple）：广告命名模板（已实现，广告系列/广告组两组）+ 红线规则配置
 *     （已实现，投放时间 + 豁免关键词）+ 策略配置 + 策略运行记录（2 项空页）
 *   - 荣耀（honor）/ VIVO（vivo）：广告命名模板 + 红线规则配置 + 策略配置 + 策略运行记录（4 项，均为空页）
 *   - 巨量广告（ocean）/ 百度广告（baidu）：
 *     红线规则配置 + 策略配置 + 策略运行记录（3 项，均为空页）
 *   - 我的资源（my-resources）：展业城市（跨平台共享展业地域维表只读页，1 项，已实现）
 *
 * 冻结机制：外层壳 `h-screen + overflow-hidden`，侧栏为行内 flex 兄弟，
 * 高度随行高固定，`main` 为唯一滚动容器，用户下拉页面时各平台侧栏位置均不变。
 */

import { NavLink } from "react-router-dom";

export type PlatformKey =
  | "tencent"
  | "ocean"
  | "baidu"
  | "huawei"
  | "apple"
  | "honor"
  | "vivo"
  | "my-resources";

interface MenuItem {
  key: string;
  label: string;
  to: string;
  icon: React.ReactNode;
}

/** 平台品牌短标（侧栏顶部小标签）与无障碍名 */
const PLATFORM_META: Record<PlatformKey, { brand: string; aria: string }> = {
  tencent: { brand: "Tencent", aria: "腾讯广告功能导航" },
  ocean: { brand: "Ocean", aria: "巨量广告功能导航" },
  baidu: { brand: "Baidu", aria: "百度广告功能导航" },
  huawei: { brand: "Huawei", aria: "华为应用商店功能导航" },
  apple: { brand: "Apple", aria: "Apple Ads 功能导航" },
  honor: { brand: "Honor", aria: "荣耀功能导航" },
  vivo: { brand: "vivo", aria: "VIVO 功能导航" },
  "my-resources": { brand: "Resources", aria: "我的资源功能导航" },
};

// —— 图标（LEO §6.8 内联 SVG，stroke=currentColor，1.75 线宽）——
const IconNameTemplate = (
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6" />
    <path d="M8 13h8M8 17h5" />
  </svg>
);
const IconRedline = (
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const IconStrategy = (
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9 1.7 1.7 0 0 0 4.3 7.2l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9c.3.6.9 1 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
);
const IconResult = (
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="1" />
    <path d="M9 12h6M9 16h4" />
  </svg>
);

/** 我的资源 - 展业城市：地图钉（LEO §6.8 内联 SVG，stroke=currentColor） */
const IconCity = (
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 21s-7-5.2-7-11a7 7 0 0 1 14 0c0 5.8-7 11-7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

/** 腾讯广告 - DashBoard：柱状图（LEO §6.8 内联 SVG，stroke=currentColor） */
const IconDashboard = (
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </svg>
);

/** 腾讯广告 - T0广告看板：看板网格（LEO §6.8 内联 SVG，stroke=currentColor） */
const IconT0Board = (
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9h18M9 9v11" />
  </svg>
);

/** 腾讯广告 - 授权更新：二维码（LEO §6.8 内联 SVG，stroke=currentColor） */
const IconQrAuth = (
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="4" width="6" height="6" rx="1" />
    <rect x="14" y="4" width="6" height="6" rx="1" />
    <rect x="4" y="14" width="6" height="6" rx="1" />
    <path d="M14 14h.01M17.5 14h.01M20 14h.01M14 17.5h.01M14 20h.01M17.5 17.5h.01M20 17.5h.01M17.5 20h.01M20 20h.01" />
  </svg>
);

/**
 * 全平台共用的「红线规则配置 / 策略配置 / 策略运行记录」三项。
 * 路径按平台前缀生成（如 `/ocean/redline`）。
 */
function sharedMenu(platform: PlatformKey): MenuItem[] {
  return [
    { key: "redline", label: "红线规则配置", to: `/${platform}/redline`, icon: IconRedline },
    { key: "strategy", label: "策略配置", to: `/${platform}/strategy`, icon: IconStrategy },
    {
      key: "auto-operate-result",
      label: "策略运行记录",
      to: `/${platform}/auto-operate-result`,
      icon: IconResult,
    },
  ];
}

/** 腾讯/华为/Apple Ads 额外含「广告命名模板」入口（腾讯/华为/Apple Ads 为已实现的编辑器页，荣耀/VIVO 为空页占位），置于三项之前 */
function buildMenu(platform: PlatformKey): MenuItem[] {
  if (platform === "my-resources") {
    // 我的资源：跨平台共享资源（展业地域维表只读页），不挂平台前缀。
    return [
      {
        key: "business-city",
        label: "展业城市",
        to: "/my-resources/business-city",
        icon: IconCity,
      },
    ];
  }
  if (
    platform === "tencent" ||
    platform === "huawei" ||
    platform === "apple" ||
    platform === "honor" ||
    platform === "vivo"
  ) {
    return [
      {
        key: "ad-name-template",
        label: "广告命名模板",
        to: `/${platform}/ad-name-template`,
        icon: IconNameTemplate,
      },
      ...sharedMenu(platform),
      // 腾讯广告专属：T0广告看板（筛选占位 + 自定义账户集合）+
      // DashBoard（策略执行结果按操作原因聚合）+ 授权更新（USER_TOKEN 扫码）。
      ...(platform === "tencent"
        ? [
            {
              key: "t0-dashboard",
              label: "T0广告看板",
              to: "/tencent/t0-dashboard",
              icon: IconT0Board,
            },
            {
              key: "auto-operate-dashboard",
              label: "DashBoard",
              to: "/tencent/auto-operate-dashboard",
              icon: IconDashboard,
            },
            {
              key: "user-token-auth",
              label: "授权更新",
              to: "/tencent/user-token-auth",
              icon: IconQrAuth,
            },
          ]
        : []),
    ];
  }
  return sharedMenu(platform);
}

export function Sidebar({ platform }: { platform: PlatformKey }) {
  const meta = PLATFORM_META[platform];
  const menu = buildMenu(platform);
  return (
    <aside
      className="flex w-[88px] flex-col items-center gap-1.5 border-r border-line-subtle bg-da-panel-left py-4 md:w-[108px]"
      aria-label={meta.aria}
    >
      <div className="mb-2 px-2 text-center text-[10px] uppercase tracking-[0.18em] text-text-muted-weak">
        {meta.brand}
      </div>
      {menu.map((item) => (
        <NavLink
          key={item.key}
          to={item.to}
          className={({ isActive }) =>
            `leo-sideitem ${isActive ? "is-active" : ""}`
          }
          end
        >
          <span className="grid h-9 w-9 place-items-center">{item.icon}</span>
          <span className="px-1 text-center leading-tight">{item.label}</span>
        </NavLink>
      ))}
    </aside>
  );
}
